import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import { Input } from './input';
import { Label } from './label';
import { cn } from '../../lib/utils';
import { useLanguage } from '../../contexts/LanguageContext';
import 'flag-icons/css/flag-icons.min.css';

const AuthLayout = ({ title, subtitle, children, footer }) => {
  const { language, setLanguage, t } = useLanguage();
  const [languageMenuOpen, setLanguageMenuOpen] = useState(false);
  const languageMenuRef = useRef(null);

  useEffect(() => {
    if (!languageMenuOpen) return undefined;
    const closeOnOutsideClick = (event) => {
      if (languageMenuRef.current && !languageMenuRef.current.contains(event.target)) {
        setLanguageMenuOpen(false);
      }
    };
    const closeOnEscape = (event) => {
      if (event.key === 'Escape') setLanguageMenuOpen(false);
    };
    document.addEventListener('mousedown', closeOnOutsideClick);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('mousedown', closeOnOutsideClick);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [languageMenuOpen]);

  const languages = [
    { code: 'sq', flag: 'al', label: 'Shqip' },
    { code: 'de', flag: 'de', label: 'Gjermanisht' },
  ];
  const selectedLanguage = languages.find((item) => item.code === language) || languages[0];

  return (
    <div className="relative min-h-screen h-screen overflow-hidden bg-white dark:bg-[#0a0a0c] flex items-center justify-center p-0 font-sans lg:p-2.5">
      {/* Left Panel: App Showcase (Visible on Large Screens) */}
      <div className="hidden lg:flex lg:min-h-[calc(100vh-1.25rem)] lg:w-[51%] bg-black relative overflow-hidden flex-col justify-between rounded-[28px] p-14 text-white">
        {/* Decorative Gradients & Ambient Glow */}
        <div className="absolute -left-24 -top-24 h-64 w-64 rounded-full border-4 border-zinc-800/70 pointer-events-none" />
        <div className="absolute -bottom-48 -right-24 h-80 w-80 rounded-full border-4 border-zinc-800/70 pointer-events-none" />
        <div className="absolute -right-32 top-1/3 h-96 w-96 rounded-full bg-[#DD0000]/20 blur-[120px] pointer-events-none" />
        <div className="absolute -bottom-24 left-10 h-72 w-72 rounded-full bg-[#FFCE00]/10 blur-[110px] pointer-events-none" />

        {/* Grid Pattern overlay */}
        <div
          className="absolute inset-0 opacity-[0.03] pointer-events-none"
          style={{
            backgroundImage: `radial-gradient(circle, #fff 1px, transparent 1px)`,
            backgroundSize: '24px 24px',
          }}
        />

        {/* Header / Brand */}
        <div className="relative z-10">
          <Link to="/" className="inline-flex items-center gap-2.5">
            <img src="/logo.png" alt="DeutschLernen" className="h-auto w-56 brightness-0 invert" />
          </Link>
        </div>

        {/* Main Content Info */}
        <div className="relative z-10 my-auto max-w-lg space-y-8 animate-fadeIn">
          <div className="space-y-4">
            <div aria-hidden="true" className="flex h-1.5 w-24 overflow-hidden rounded-full">
              <span className="flex-1 bg-zinc-600" />
              <span className="flex-1 bg-[#DD0000]" />
              <span className="flex-1 bg-[#FFCE00]" />
            </div>
            <h2 className="text-5xl font-light leading-[1.08] tracking-tight">
              {t('Mëso gjermanisht.')}
              <br />
              {t('Fol më lirshëm.')}
              <br />
              {t('Arrij B2.')}
            </h2>
            <p className="max-w-md text-sm leading-relaxed text-zinc-400">
              {t('Platforma jote për fjalor, gramatikë dhe praktikë reale në gjuhën gjermane.')}
            </p>
          </div>
        </div>

        {/* Footer Info */}
        <div className="relative z-10 text-xs text-zinc-500">
          &copy; {new Date().getFullYear()} DeutschLernen
        </div>
      </div>

      {/* Right Panel: Login / Auth Form */}
      <div className="relative h-screen w-full lg:w-[49%] flex items-center justify-center p-6 sm:p-12 overflow-y-auto">
        {/* Floating Language Switcher */}
        <div className="absolute right-6 top-5 z-20" ref={languageMenuRef}>
          <button
            type="button"
            onClick={() => setLanguageMenuOpen((open) => !open)}
            aria-label={t('Zgjidh gjuhën')}
            aria-expanded={languageMenuOpen}
            className="flex h-11 min-w-[155px] items-center gap-2.5 rounded-xl border border-zinc-200 dark:border-white/10 bg-white dark:bg-zinc-900 px-3.5 text-sm font-semibold text-zinc-900 dark:text-zinc-100 transition hover:bg-zinc-50 dark:hover:bg-zinc-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-300"
          >
            <span className={`fi fi-${selectedLanguage.flag}`} aria-hidden="true" />
            <span className="flex-1 text-left">{t(selectedLanguage.label)}</span>
            <ChevronDown size={16} className="text-zinc-400" />
          </button>
          {languageMenuOpen && (
            <div
              aria-label={t('Zgjidh gjuhën')}
              className="absolute right-0 top-full mt-2 w-full overflow-hidden rounded-2xl border border-zinc-200/80 dark:border-white/15 bg-white/95 dark:bg-zinc-900/95 backdrop-blur-2xl p-1.5 text-zinc-900 dark:text-zinc-100 shadow-[0_16px_40px_rgba(0,0,0,0.14)] animate-scaleIn"
            >
              {languages.map((item) => (
                <button
                  key={item.code}
                  type="button"
                  onClick={() => {
                    setLanguage(item.code);
                    setLanguageMenuOpen(false);
                  }}
                  aria-pressed={language === item.code}
                  className={`flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-sm transition-colors hover:bg-zinc-100 dark:hover:bg-zinc-800 ${
                    language === item.code ? 'bg-zinc-100 dark:bg-zinc-800 font-semibold' : ''
                  }`}
                >
                  <span className={`fi fi-${item.flag}`} aria-hidden="true" />
                  <span>{t(item.label)}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Form container (plain, no card) */}
        <div className="relative z-10 w-full max-w-[400px] space-y-7 animate-fadeIn">
          {/* Mobile Header (Hidden on Desktop) */}
          <div className="flex justify-center lg:hidden">
            <Link to="/" className="inline-flex items-center gap-2">
              <img src="/logo.png" alt="DeutschLernen" className="h-auto w-48" />
            </Link>
          </div>

          {/* Heading */}
          <div className="space-y-2 text-center">
            <h1 className="text-2xl font-semibold tracking-tight text-zinc-950 dark:text-zinc-50">{t(title)}</h1>
            {subtitle && (
              <p className="text-xs leading-normal text-zinc-500 dark:text-zinc-400">{t(subtitle)}</p>
            )}
          </div>

          {/* Form content */}
          <div>
            {children}
          </div>

          {/* Footer Link */}
          {footer && (
            <p className="border-t border-zinc-200 dark:border-white/10 pt-5 text-center text-xs text-zinc-500 dark:text-zinc-400">
              {footer}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

// ── Shared sub-components ─────────────────────────────────────────────────────

export const AuthInput = ({ icon: Icon, className = '', ...props }) => (
  <div className="relative">
    {Icon && (
      <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
        <Icon className="h-4 w-4 text-zinc-400" />
      </div>
    )}
    <Input
      className={cn(
        'h-11 border-zinc-200/75 dark:border-white/10 bg-white/70 dark:bg-zinc-800/50 backdrop-blur-xs focus:bg-white dark:focus:bg-zinc-800 focus:border-zinc-400 focus:ring-zinc-300 transition-all rounded-xl text-sm shadow-xs',
        Icon && 'pl-10',
        className
      )}
      {...props}
    />
  </div>
);

export const AuthLabel = ({ children, htmlFor, className }) => (
  <Label
    htmlFor={htmlFor}
    className={cn('text-[11px] font-semibold text-zinc-700 dark:text-zinc-300 uppercase tracking-wider', className)}
  >
    {children}
  </Label>
);

export const AuthError = ({ children }) => (
  <div
    role="alert"
    className="rounded-xl border border-red-200/80 bg-red-50/70 dark:bg-red-950/40 dark:border-red-900/50 px-4 py-3 text-xs text-red-700 dark:text-red-300 backdrop-blur-xs animate-slideIn"
  >
    {children}
  </div>
);

export const AuthLink = ({ to, children, className }) => (
  <Link
    to={to}
    className={cn(
      'font-semibold text-zinc-900 dark:text-zinc-100 underline underline-offset-4 hover:text-[#DD0000] dark:hover:text-[#FFCE00] transition-colors',
      className,
    )}
  >
    {children}
  </Link>
);

export const SocialLoginButtons = ({ onProvider, loading = false }) => (
  <div className="grid grid-cols-2 gap-3">
    <button
      type="button"
      disabled={loading}
      onClick={() => onProvider('google')}
      className="flex h-11 items-center justify-center gap-2 rounded-xl border border-zinc-200/75 dark:border-white/10 bg-white/75 dark:bg-zinc-800/60 backdrop-blur-xs text-sm font-semibold text-zinc-800 dark:text-zinc-200 transition-all duration-200 hover:border-zinc-300 hover:bg-white dark:hover:bg-zinc-800 hover:shadow-xs active:scale-98 disabled:opacity-50"
    >
      <svg viewBox="0 0 24 24" className="h-4 w-4" aria-hidden="true">
        <path fill="#4285F4" d="M21.35 12.22c0-.72-.06-1.42-.18-2.09H12v3.96h5.24a4.48 4.48 0 0 1-1.94 2.94v2.45h3.14c1.84-1.69 2.91-4.18 2.91-7.26Z" />
        <path fill="#34A853" d="M12 21.5c2.63 0 4.84-.87 6.45-2.36l-3.14-2.45c-.87.58-1.98.92-3.31.92-2.54 0-4.69-1.72-5.46-4.03H3.3v2.53A9.74 9.74 0 0 0 12 21.5Z" />
        <path fill="#FBBC05" d="M6.54 13.58a5.86 5.86 0 0 1 0-3.16V7.89H3.3a9.5 9.5 0 0 0 0 8.22l3.24-2.53Z" />
        <path fill="#EA4335" d="M12 6.39c1.43 0 2.71.49 3.72 1.46l2.79-2.79C16.84 3.48 14.63 2.5 12 2.5a9.74 9.74 0 0 0-8.7 5.39l3.24 2.53C7.31 8.11 9.46 6.39 12 6.39Z" />
      </svg> Google
    </button>
    <button
      type="button"
      disabled={loading}
      onClick={() => onProvider('azure')}
      className="flex h-11 items-center justify-center gap-2 rounded-xl border border-zinc-200/75 dark:border-white/10 bg-white/75 dark:bg-zinc-800/60 backdrop-blur-xs text-sm font-semibold text-zinc-800 dark:text-zinc-200 transition-all duration-200 hover:border-zinc-300 hover:bg-white dark:hover:bg-zinc-800 hover:shadow-xs active:scale-98 disabled:opacity-50"
    >
      <svg viewBox="0 0 24 24" className="h-4 w-4" aria-hidden="true">
        <path fill="#f25022" d="M2 2h9.5v9.5H2z" /><path fill="#7fba00" d="M12.5 2H22v9.5h-9.5z" />
        <path fill="#00a4ef" d="M2 12.5h9.5V22H2z" /><path fill="#ffb900" d="M12.5 12.5H22V22h-9.5z" />
      </svg>
      Microsoft
    </button>
  </div>
);

export default AuthLayout;
